import { unwrap } from "@sagot/api-client";
import { AUDIT_PREFIXES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { datedFilename, saveBlob } from "../../../engage/shared/download";

/** The audit trails the privacy page filters by: launches, customers (lookups, views, context) and API keys. */
export const TRAILS = ["all", "launch", "customer", "api_key"] as const;
export type Trail = (typeof TRAILS)[number];
/** Query-string key of the audit tab (`?trail=customer`). */
export const TRAIL_PARAM = "trail";

export function parseTrail(value: string | null): Trail {
  return TRAILS.find((trail) => trail === value) ?? "all";
}

/** `launch.` for the launch trail, `null` for all activity. */
export function trailPrefix(trail: Trail): string | null {
  return AUDIT_PREFIXES.find((prefix) => prefix === `${trail}.`) ?? null;
}

/** The filters both the table and its CSV take (`/v1/audit-logs` and `/v1/audit-logs/export`). */
export interface AuditQuery {
  q?: string | null;
  since?: string | null;
  until?: string | null;
  actor_id?: string | null;
  action_prefix?: string | null;
}

/** `GET /v1/audit-logs/export` with the filters on screen (the server writes up to 50,000 rows; needs `audit.view` and `data.export`). */
export function useAuditCsv() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  return useMutation({
    mutationFn: (query: AuditQuery) => unwrap(api.GET("/v1/audit-logs/export", { params: { query }, parseAs: "blob" })),
    onSuccess: (blob) => {
      saveBlob(blob, datedFilename("audit-log", "csv"));
      toast.success(t("privacyM29.audit.exported"));
    },
    onError: (caught) => toast.error(`${t("privacyM29.audit.exportFailed")}: ${message(caught)}`),
  });
}

/** "launch.minted" -> "launch_minted": the i18n key of an audit action (no dots inside a key segment). */
export const actionKey = (action: string): string => action.replace(/\./g, "_");
